import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { DatePicker, MaskedInput, Select, TextArea } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { format } from "date-fns";
import isNull from "lodash/isNull";
import { useCallback, useState } from "react";
import {
  useCreateMsaDecisionLineItemMutation,
  useDeleteMsaDecisionLineItemByIdMutation,
  useDeleteMSADecisionMutation,
  usePatchMSADecisionMutation,
} from "services/msa-api";
import type {
  MSAAllocation,
  MSADecisionLineItemType,
  MSADecisionType,
} from "types/DataTypes";
import { STRING_TO_DATE } from "utils/date-utils";
import { formatCurrency } from "utils/format-utils";
import { useSelector } from "utils/redux";

import CMSDecisionModal from "./CMSDecisionModal";
import { CMSLineItemTypeModal } from "./CMSLineItemTypeModal";

type MSADecisionStatusProps = {
  msaId: string;
  decision: MSADecisionType;
};

const MSADecisionTotalRow = ({ decision }: { decision: MSADecisionType }) => {
  const isRxChangeNegative = decision?.rx_differential_total < 0;
  const totalChangeRx = `${decision?.rx_differential_total?.toFixed(1)}%`;
  const rxArrowIcon = isRxChangeNegative ? "arrow_downward" : "arrow_upward";

  const isMedicalChangeNegative = decision?.medical_differential_total < 0;
  const totalChangeMedical = `${decision?.medical_differential_total?.toFixed(1)}%`;

  const medicalArrowIcon = isMedicalChangeNegative
    ? "arrow_downward"
    : "arrow_upward";

  return (
    <>
      <label>Medical Total:</label>
      <figure>{formatCurrency(decision?.proposed_total_medical)}</figure>
      <figure>{formatCurrency(decision?.decision_total_medical)}</figure>
      {decision?.medical_differential_total != null ? (
        <figure
          className={cx("txt-color", {
            red: isMedicalChangeNegative,
            green: !isMedicalChangeNegative,
          })}
        >
          {totalChangeMedical}
          <span className="material-icons">{medicalArrowIcon}</span>
        </figure>
      ) : (
        <div></div>
      )}

      <label>Rx Total:</label>
      <figure>{formatCurrency(decision?.proposed_total_prescription)}</figure>
      <figure>{formatCurrency(decision?.decision_total_prescription)}</figure>
      {decision?.rx_differential_total != null ? (
        <figure
          className={cx("txt-color", {
            red: isRxChangeNegative,
            green: !isRxChangeNegative,
          })}
        >
          {totalChangeRx}
          <span className="material-icons">{rxArrowIcon}</span>
        </figure>
      ) : (
        <div></div>
      )}
    </>
  );
};

// line item row
const MSADecisionRow = ({
  decisionItem,
  onOpenModal,
}: {
  onOpenModal: () => void;
  decisionItem: MSADecisionLineItemType;
}) => {
  const proposed: MSAAllocation =
    decisionItem?.proposed_treatment_allocation ||
    decisionItem?.proposed_prescription_allocation;

  const decision: MSAAllocation =
    decisionItem?.decision_treatment_allocation ||
    decisionItem?.decision_prescription_allocation;

  const totalChangeExists =
    isNull(decisionItem?.total_percent_change) === false;
  const totalChange = totalChangeExists
    ? `${decisionItem?.total_percent_change?.toFixed(1)}%`
    : "---";
  const isChangeNegative =
    decisionItem?.total_percent_change &&
    decisionItem?.total_percent_change < 0;

  const handleClickItem = () => {
    onOpenModal();
  };

  const arrowIcon = isChangeNegative ? "arrow_downward" : "arrow_upward";

  return (
    <div className="decision-row" onClick={handleClickItem}>
      <label>
        <a className="txt-btn">{decision?.name || "MISSING"}:</a>
      </label>
      <figure>{formatCurrency(proposed?.total_cost)}</figure>
      <figure>{formatCurrency(decision?.total_cost)}</figure>
      <figure
        className={cx("txt-color", {
          red: totalChangeExists && isChangeNegative,
          green: totalChangeExists && !isChangeNegative,
        })}
      >
        {totalChange}
        {totalChangeExists && (
          <span className="material-icons">{arrowIcon}</span>
        )}
      </figure>
    </div>
  );
};

const MSADecisionStatus = ({ msaId, decision }: MSADecisionStatusProps) => {
  const constants = useSelector((state) => state.shared.constants);
  const [item, setItem] = useState<MSADecisionLineItemType | null>(null);
  const [selectedDecisionId, setSelectedDecisionId] = useState<number | null>(
    null,
  );
  const [decisionToDelete, setDecisionToDelete] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [approvalAmount, setApprovalAmount] = useState<string>(
    decision.amount_total ? decision.amount_total.toString() : "",
  );

  const [createDecisionLineItem] = useCreateMsaDecisionLineItemMutation();
  const [patchMSADecision] = usePatchMSADecisionMutation();
  const [deleteDecisionLineItem] = useDeleteMsaDecisionLineItemByIdMutation();
  const [deleteMSADecision, { isLoading: isDeleting }] =
    useDeleteMSADecisionMutation();

  const [rationaleValue, setRationaleValue] = useState<string | null>(
    decision.cms_rationale || "",
  );

  const [refreshKey, setRefreshKey] = useState(0);

  const handleSave = useCallback(
    async (decisionId: string, field: string, value: unknown) => {
      try {
        await patchMSADecision({
          msaId,
          decisionId,
          data: { [field]: value },
        }).unwrap();
        KaboodleToaster.success("Saved Successfully");
      } catch (error) {
        KaboodleToaster.error(
          `Failed to update field: ${error.data?.[field]?.[0] || "Unknown error"}`,
        );
      }
    },
    [patchMSADecision, msaId],
  );

  const handleRationaleChange = (
    value: string | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newValue = typeof value === "string" ? value : value.target.value;
    setRationaleValue(newValue === "" ? null : newValue);
  };

  const handleApprovalAmountChange = (event: {
    target: { value: string; name: string };
  }) => {
    setApprovalAmount(event.target.value);
  };

  const handleApprovalAmountBlur = () => {
    const numericValue = parseFloat(approvalAmount.replace(/[^0-9.-]+/g, ""));
    if (!isNaN(numericValue)) {
      handleSave(decision.id.toString(), "amount_total", numericValue);
    }
  };

  const handleCreateLineItem = useCallback(
    async (id: number | null, data: Record<string, string>) => {
      try {
        await createDecisionLineItem({
          data: {
            msa_decision: id,
            allocation_type: data.allocationType,
            name: data.name,
          },
        }).unwrap();
        setSelectedDecisionId(null);
      } catch (error) {
        console.error("API Error:", error);
        const errorMessage =
          error?.data?.detail?.[0] || error?.data?.message || "Opps";
        KaboodleToaster.error(errorMessage);
      }
    },
    [createDecisionLineItem],
  );

  const handleDelete = async () => {
    if (!decisionToDelete) return;

    try {
      await deleteMSADecision({
        decisionId: decisionToDelete.toString(),
        confirmed: true,
      }).unwrap();

      setShowDeleteConfirm(false);
      setDecisionToDelete(null);
      KaboodleToaster.success("Decision deleted successfully");
    } catch (error) {
      KaboodleToaster.error(error?.data?.detail || "Failed to delete decision");
    }
  };

  const openDeleteModal = (decisionId: number) => {
    setDecisionToDelete(decisionId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteLineItem = async () => {
    if (!item) return;
    try {
      await deleteDecisionLineItem({
        id: item.id.toString(),
      }).unwrap();
      KaboodleToaster.success("Line item deleted successfully");
      setItem(null);
      setRefreshKey((prevKey) => prevKey + 1); // Trigger re-render
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.detail || "Failed to delete line item",
      );
    }
  };

  return (
    <>
      <CMSDecisionModal
        isOpen={!!item}
        decisionItem={item}
        onClose={() => {
          setItem(null);
        }}
        onDelete={handleDeleteLineItem}
      />
      <CMSLineItemTypeModal
        isOpen={!!selectedDecisionId}
        selectedDecisionId={selectedDecisionId}
        onClose={() => {
          setSelectedDecisionId(null);
        }}
        onCreate={handleCreateLineItem}
      />
      <KaboodleModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        footer={
          <div className="flex jc-c">
            <Button
              className="delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Confirm Delete
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          </div>
        }
        children={
          <Card heading="Delete Decision">
            <p>Are you sure you want to delete this decision?</p>
          </Card>
        }
      />
      <div className="data-block max-content-w">
        <div className="data-block-header flex jc-sb">
          CMS DECISION {decision.re_review ? "(Re-Review)" : ""}
          <div className="flex jc-end">
            {decision.deletable && (
              <ButtonIcon
                size="small"
                onClick={() => openDeleteModal(decision.id)}
                title="Delete Decision"
              >
                delete
              </ButtonIcon>
            )}
            <ButtonIcon
              kind="neutral"
              size="small"
              onClick={() => {
                setSelectedDecisionId(decision.id);
              }}
            >
              add_circle
            </ButtonIcon>
          </div>
        </div>

        <div className="data-values">
          <label>Type:</label>
          <Select
            name={`decision-${decision.id}-type`}
            options={constants?.CMS_MSA_DECISION_TYPE || []}
            value={
              constants?.CMS_MSA_DECISION_TYPE?.find(
                (option) => option.value === decision.decision_type,
              ) || null
            }
            onChange={(selectedOption: OptionType) => {
              const value = selectedOption?.value;
              handleSave(decision.id.toString(), "decision_type", value);
            }}
            placeholder="Select type"
          />
          <label>Decision Date:</label>
          <DatePicker
            name={`decision-${decision.id}-decision-date`}
            onDateChange={(date) =>
              handleSave(
                decision.id.toString(),
                "decision_date",
                format(date, "yyyy-MM-dd"),
              )
            }
            initialDate={
              decision.decision_date
                ? STRING_TO_DATE(decision.decision_date)
                : undefined
            }
          />
          <label>Date Received:</label>
          <DatePicker
            name={`decision-${decision.id}-date-received`}
            onDateChange={(date) =>
              handleSave(
                decision.id.toString(),
                "date_received",
                format(date, "yyyy-MM-dd"),
              )
            }
            initialDate={
              decision.date_received
                ? STRING_TO_DATE(decision.date_received)
                : undefined
            }
          />
          <label>Date Submitted:</label>
          <DatePicker
            name={`decision-${decision.id}-date-submitted`}
            onDateChange={(date) =>
              handleSave(
                decision.id.toString(),
                "date_submitted",
                format(date, "yyyy-MM-dd"),
              )
            }
            initialDate={
              decision.date_submitted
                ? STRING_TO_DATE(decision.date_submitted)
                : undefined
            }
          />
          <div className="border grid-2-wide"></div>
        </div>

        <div className="data-values cms-decision">
          <label>
            <b>ITEM</b>
          </label>
          <label>
            <b>PROPOSED</b>
          </label>
          <label>
            <b>DECISION</b>
          </label>
          <label>
            <b>Result</b>
          </label>

          {decision?.line_items.map((lineItem, index) => (
            <MSADecisionRow
              key={index}
              decisionItem={lineItem}
              onOpenModal={() => {
                setItem(lineItem);
              }}
            />
          ))}

          <div className="border grid-4-wide"></div>

          <MSADecisionTotalRow decision={decision} />
        </div>

        <div className="data-values">
          <div className="border grid-2-wide"></div>

          <label>Approval Amount Total:</label>
          <MaskedInput
            name={`decision-${decision.id}-amount-total`}
            mask="dollar"
            value={approvalAmount}
            onChange={handleApprovalAmountChange}
            onBlur={handleApprovalAmountBlur}
          />

          <label>CMS Rationale:</label>
          <TextArea
            key={`cms-rationale-${decision.id}-${refreshKey}`}
            name={`decision-${decision.id}-cms-rationale`}
            label=""
            value={rationaleValue || ""}
            advancedEdit={true}
            onSave={async (value) => {
              handleRationaleChange(value);
              await handleSave(decision.id.toString(), "cms_rationale", value);
            }}
            placeholder="Enter rationale"
          />
        </div>
      </div>
    </>
  );
};

export default MSADecisionStatus;
